import { useMemo, useState, useEffect } from 'react';
import Button from '@components/Button';
import FiltersModal from '@components/FiltersModal';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { FiltersIcon } from 'assets/images/icons';
import { POST_CHANNEL_LABEL, FILTERABLE_CAMPAIGN_STATUSES, CAMPAIGN_STATUS_LABEL_KEY } from '@constants/social-posts';
import { AccIntegrationName } from '@constants/integrations';
import { useTranslation, useLocations } from '@hooks';

const FiltersPanel = ({ onApplyFilters, filter, setFilter, availablePlatforms = [] }) => {
    const [isFilterOpened, setFilterOpened] = useState(false);
    const [refetchFilters, setRefetchFilters] = useState(false);
    const [filterNumber, setFilterNumber] = useState(0);

    const { t } = useTranslation();
    const { getLocationsOptions } = useLocations();

    useEffect(() => {
        if (refetchFilters) {
            setRefetchFilters(false);
            onApplyFilters();
        }
    }, [refetchFilters]);

    const options = useMemo(() => {
        const locationFilterCb = l =>
            l?.profiles?.some(p => p.type === AccIntegrationName.Google || p.type === AccIntegrationName.Facebook);
        const locationsOptions = getLocationsOptions(locationFilterCb);

        const platformsOptions = availablePlatforms
            .filter(value => POST_CHANNEL_LABEL[value])
            .map(value => ({ value, label: POST_CHANNEL_LABEL[value] }));

        const statusesOptions = FILTERABLE_CAMPAIGN_STATUSES.map(value => ({
            value,
            label: t(CAMPAIGN_STATUS_LABEL_KEY[value]),
        }));

        return {
            companies: locationsOptions,
            platforms: platformsOptions,
            statuses: statusesOptions,
        };
    }, [filter, t, availablePlatforms]);

    useEffect(() => {
        const number = Object.keys(filter).reduce((total, key) => {
            if (!Array.isArray(filter[key])) return total;
            const narrowed = filter[key].length !== options[key]?.length ? 1 : 0;
            return total + narrowed;
        }, 0);
        setFilterNumber(number);
    }, [filter, options]);

    const update = {
        companies: items => setFilter(prev => ({ ...prev, companies: items.length ? items : [] })),
        platforms: value => setFilter(prev => ({ ...prev, platforms: value })),
        statuses: value => setFilter(prev => ({ ...prev, statuses: value })),
    };

    const formOptions = Object.keys(filter)
        .filter(key => Array.isArray(filter[key]))
        .map(key => ({
            type: key === 'companies' ? 'autocomplete' : 'multiSelect',
            label: t(`filters.${key}`),
            value: filter[key],
            onChange: update[key],
            options: options[key],
            countable: true,
            error: !filter[key].length,
        }));

    const isValid = Object.keys(filter).every(key => {
        if (Array.isArray(filter[key])) {
            return !!filter[key].length;
        }
        return true;
    });

    const resetFilters = () =>
        setFilter(prev => ({
            ...prev,
            companies: options.companies,
            platforms: options.platforms.map(option => option.value),
            statuses: options.statuses.map(option => option.value),
        }));

    return (
        <>
            <Button
                sx={{
                    minWidth: '40px',
                    '& .MuiButton-startIcon svg': {
                        width: 'auto',
                        height: 'auto',
                    },
                }}
                variant="outlined"
                onClick={() => setFilterOpened(!isFilterOpened)}
                startIcon={<FiltersIcon />}
            >
                {t('buttons.filters')}
                {filterNumber > 0 ? ` (${filterNumber})` : ''}
            </Button>
            <FiltersModal
                isOpen={isFilterOpened}
                handleClose={() => setFilterOpened(false)}
                onApply={() => {
                    setRefetchFilters(true);
                    setFilterOpened(false);
                }}
                applyDisabled={!isValid}
                onClear={resetFilters}
                clearDisabled={filterNumber === 0}
            >
                <FiltersFormCreator type="social-posts" options={formOptions} />
            </FiltersModal>
        </>
    );
};

export default FiltersPanel;
